import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import itLocale from 'date-fns/locale/it';
import styled from 'styled-components';
import {
    CAC_TYPE,
    ESTIMATED_SEEDLINGS,
    ESTIMATED_YIELD,
    ReportCharactersNames,
    USER_ROLES
} from '../../../../../res/Constants';
import {
    extractLinesData,
    REPORT_HARVEST_DATA,
    REPORT_YIELD
} from '../../../../../service/ReportService';
import { isReportCharacterNumericValid } from './../../../../../service/CommonFunctions';
import { getUsers } from '../../../../../service/UserService';
import { getSettingList } from '../../../../../service/SettingService';
import { getCurrentCachedUserData } from '../../../../../service/AccountService';
import { getHarvestLines, roleLabels } from '../../../../../utils/Utils';
import 'react-datepicker/dist/react-datepicker.css';
import EditableLine from './EditableLine';

registerLocale('it', itLocale);

EditableReportItem.propTypes = {
    linesCount: PropTypes.number,
    reportData: PropTypes.object,
    cacType: PropTypes.number,
    contractHarvest: PropTypes.string,
    onReportEdited: PropTypes.func,
    isNewReport: PropTypes.bool,
    isAdmin: PropTypes.bool
};

EditableReportItem.defaultProps = {
    reportData: {},
    onReportEdited: () => { },
    isNewReport: false,
    isAdmin: false
};

const NUMERIC_FIELDS = new Set([ReportCharactersNames.stand, ReportCharactersNames.bolt]);

export default function EditableReportItem(props) {
    const [linesData, setLinesData] = useState(
        extractLinesData(props.linesCount, props.reportData)
    );
    const [invalidFields, setInvalidFields] = useState(new Set());

    // Initialize date with current date for new reports, or existing date for editing
    const [reportDate, setReportDate] = useState(
        props.reportData.date ? moment(props.reportData.date).toDate() : moment().toDate()
    );

    // Initialize user - always preset with current user when creating a new report
    // When editing existing reports, use the existing techId
    const getInitialUser = () => {
        // Check if it's a new report FIRST
        if (props.isNewReport) {
            // Creating a new report - preset with current user (both admin and non-admin)
            const currentUser = getCurrentCachedUserData();
            return currentUser?.pk?.toString() || '';
        }

        // If editing existing report, use the existing techId
        if (props.reportData.techId) {
            return props.reportData.techId;
        }

        // Fallback - should not happen
        return '';
    };

    const [reportUser, setReportUser] = useState(getInitialUser());

    // State for users list
    const [usersList, setUsersList] = useState([]);

    // State for diseases description options
    const [diseasesOptions, setDiseasesOptions] = useState([]);

    // Load users list
    useEffect(() => {
        getUsers((users) => {
            // Filter out clients (customers)
            const filteredUsers = users.filter(user => user.role !== USER_ROLES.client);
            setUsersList(filteredUsers);
        });
    }, []);

    // Convert username to pk when editing existing report
    useEffect(() => {
        if (!props.isNewReport && usersList.length > 0 && reportUser) {
            // Check if reportUser is a username (string that's not a number)
            const isUsername = isNaN(Number(reportUser));

            if (isUsername) {
                // Find user by username and get their pk
                const user = usersList.find(u => u.username === reportUser);
                if (user) {
                    setReportUser(user.pk.toString());
                }
            }
        }
    }, [usersList, props.isNewReport]);

    // Load diseases description options from settings
    useEffect(() => {
        async function fetchDiseasesOptions() {
            const settings = await getSettingList(['DiseasesDescriptions']);
            if (settings && settings.length > 0) {
                // Parse the value - it could be a comma-separated string or JSON array
                const diseasesSetting = settings[0];
                if (diseasesSetting?.value) {
                    try {
                        const options = diseasesSetting.value.split('|').map(opt => opt.trim()).filter(opt => opt);
                        setDiseasesOptions(options);
                    } catch {
                        console.log("Error parsing diseases options", diseasesSetting.value);
                        setDiseasesOptions([]);
                    }
                }
            }
        }
        fetchDiseasesOptions();
    }, []);

    // Get the correct line names based on harvest type
    const lineNames = getHarvestLines(props.cacType, props.contractHarvest);

    useEffect(() => {
        const formattedDate = reportDate ? moment(reportDate).format('YYYY-MM-DD') : '';
        const reportData = { ...linesData, date: formattedDate };
        // Pass reportUser as separate parameter (pk of user), not inside reportData
        props.onReportEdited(reportData, invalidFields.size === 0, reportUser);
    }, [linesData, invalidFields, reportDate, reportUser]);

    const onLineValuesChange = (lineName, fieldId, value) => {
        const newLineData = {
            ...linesData[lineName],
            [fieldId]: value
        };
        setLinesData({ ...linesData, [lineName]: newLineData });
        if (NUMERIC_FIELDS.has(fieldId) && !isReportCharacterNumericValid(value)) {
            setInvalidFields((currentInvalidFields) => {
                currentInvalidFields.add(fieldId);
                return currentInvalidFields;
            });
        } else {
            setInvalidFields((currentInvalidFields) => {
                currentInvalidFields.delete(fieldId);
                return currentInvalidFields;
            });
        }
    };

    const onYieldChange = (event) => {
        const value = event?.target?.value;
        if (value != null) {
            onLineValuesChange('lineU', ReportCharactersNames.expYield, value);
        }
    };

    const onHarvestDataChange = (event) => {
        const value = event?.target?.value;
        if (value != null) {
            onLineValuesChange('lineU', ReportCharactersNames.harvestData, value);
        }
    };

    const onDateChange = (date) => {
        setReportDate(date);
    };

    const onUserChange = (event) => {
        const newUserPk = event.target.value;
        setReportUser(newUserPk);
    };

    // Group users by role - memoized to avoid recalculating on every render
    const usersByRole = useMemo(() => {
        const grouped = {
            [USER_ROLES.admin]: [],
            [USER_ROLES.commercial]: [],
            [USER_ROLES.financial]: [],
            [USER_ROLES.tech]: []
        };

        usersList.forEach(user => {
            if (grouped[user.role]) {
                grouped[user.role].push(user);
            }
        });

        return grouped;
    }, [usersList]);

    return (
        <>
            <FieldContainer>
                <h5>Report Date</h5>
                <DatePickerWrapper>
                    <ReactDatePicker
                        selected={reportDate}
                        onChange={onDateChange}
                        dateFormat='dd/MM/yyyy'
                        locale='it'
                        disabled={!props.isNewReport || !props.isAdmin}
                    />
                </DatePickerWrapper>
            </FieldContainer>

            <FieldContainer>
                <h5>Reported by</h5>
                <StyledSelect
                    value={reportUser}
                    onChange={onUserChange}
                    disabled={!props.isNewReport || !props.isAdmin}
                >
                    <option value='' disabled hidden>Select a user</option>
                    {Object.entries(usersByRole)
                        .filter(([role, users]) => users.length > 0)
                        .map(([role, users]) => (
                            <optgroup key={role} label={roleLabels[role]}>
                                {users.map((user) => (
                                    <option key={user.pk} value={user.pk}>
                                        {user.username}
                                    </option>
                                ))}
                            </optgroup>
                        ))
                    }
                </StyledSelect>
            </FieldContainer>

            {linesData?.lineA?.lineName && (
                <EditableLine
                    key="lineA"
                    lineName={lineNames[0] || linesData.lineA.lineName}
                    initialData={linesData.lineA}
                    onValuesEdited={(fieldId, value) => onLineValuesChange('lineA', fieldId, value)}
                    diseasesOptions={diseasesOptions}
                    isNewReport={props.isNewReport}
                />
            )}
            {props.linesCount === 2 && linesData?.lineB?.lineName != null && (
                <EditableLine
                    key="lineB"
                    lineName={lineNames[1] || linesData.lineB.lineName}
                    initialData={linesData.lineB}
                    onValuesEdited={(fieldId, value) => onLineValuesChange('lineB', fieldId, value)}
                    diseasesOptions={diseasesOptions}
                    isNewReport={props.isNewReport}
                />
            )}
            {props.cacType === CAC_TYPE.CROP ? (
                <>
                    <h5 style={{ textAlign: 'center' }}>{ESTIMATED_YIELD}</h5>
                    <StyledInput
                        type='text'
                        value={linesData?.lineU?.[ReportCharactersNames.expYield] || ''}
                        onChange={onYieldChange}
                        id={REPORT_YIELD}
                    />
                </>
            ) : (
                <>
                    <h5 style={{ textAlign: 'center' }}>{ESTIMATED_SEEDLINGS}</h5>
                    <StyledInput
                        type='text'
                        value={linesData?.lineU?.[ReportCharactersNames.expYield] || ''}
                        onChange={onYieldChange}
                        id={REPORT_YIELD}
                    />
                    <h5 style={{ textAlign: 'center' }}>Harvest Data</h5>
                    <StyledInput
                        type='text'
                        value={linesData?.lineU?.[ReportCharactersNames.harvestData] || ''}
                        onChange={onHarvestDataChange}
                        id={REPORT_HARVEST_DATA}
                    />
                </>
            )}
        </>
    );
}

const StyledInput = styled.input`
    height: 30px;
    width: 100%;
    border-radius: 5px;
    box-shadow: 0 1px 2px grey;
    margin-bottom: 10px;
    text-indent: 10px;

    :focus {
        outline: none;
    }
    
    :disabled {
        background-color: #f0f0f0;
        cursor: not-allowed;
    }
`;

const StyledSelect = styled.select`
    height: 30px;
    width: 100%;
    border-radius: 5px;
    box-shadow: 0 1px 2px grey;
    margin-bottom: 10px;
    padding: 0 10px;

    :focus {
        outline: none;
    }
    
    :disabled {
        background-color: #f0f0f0;
        cursor: not-allowed;
    }
`;

const FieldContainer = styled.div`
    margin-bottom: 15px;
    
    h5 {
        text-align: center;
        margin-bottom: 8px;
    }
`;

const DatePickerWrapper = styled.div`
    width: 100%;
    margin-bottom: 10px;

    .react-datepicker-wrapper {
        width: 100%;
        display: block;
    }
    
    .react-datepicker__input-container {
        width: 100%;
        display: block;
    }
    
    .react-datepicker__input-container input {
        width: 100%;
        height: 30px;
        border-radius: 5px;
        box-shadow: 0 1px 2px grey;
        padding: 0 10px;
        box-sizing: border-box;

        &:focus {
            outline: none;
        }
        
        &:disabled {
            background-color: #f0f0f0;
            cursor: not-allowed;
        }
    }

    .react-datepicker__navigation-icon::before {
        border-color: #666 !important;
    }

    .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
        border-color: #333 !important;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--keyboard-selected {
        background-color: #26B566 !important;
        color: white !important;
        font-weight: bold;
    }

    .react-datepicker__day--selected:hover,
    .react-datepicker__day--keyboard-selected:hover {
        background-color: #0B924D !important;
    }
`;
